import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import tICoverImage from '../assets/images/ti-cover.jpg';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageTransitionWrapper from '../components/layout/PageTransitionWrapper';
// import Maintenance from '../components/common/Maintenance';

// --- LOGO PELKAT ---
import logoPA from '../assets/images/PA.png';
import logoPT from '../assets/images/PT.png';
import logoGP from '../assets/images/GP.png';
import logoPKP from '../assets/images/PKP.png';
import logoPKB from '../assets/images/PKB.png';
import logoPKLU from '../assets/images/PKLU.png';

// --- CONFIGURATION ---
const MAIN_CATEGORIES = [
    { name: 'Minggu', tab: 'minggu', endpoint: 'ti_minggu', icon: '&#9733;' },
    { name: 'Keluarga', tab: 'keluarga', endpoint: 'ti_ika', icon: '&#128106;' },
    { name: 'Pelkat', tab: 'pelkat', endpoint: 'ti_pelkat', icon: '&#128101;' },
    { name: 'Khusus', tab: 'khusus', endpoint: 'ti_khusus', icon: '&#128171;' },
];

const SUB_PELKAT = [
    { name: 'PA', tab: 'pa', endpoint: 'pa', icon: logoPA },
    { name: 'PT', tab: 'pt', endpoint: 'pt', icon: logoPT },
    { name: 'GP', tab: 'gp', endpoint: 'gp', icon: logoGP },
    { name: 'PKP', tab: 'pkp', endpoint: 'pkp', icon: logoPKP },
    { name: 'PKB', tab: 'pkb', endpoint: 'pkb', icon: logoPKB },
    { name: 'PKLU', tab: 'pklu', endpoint: 'pklu', icon: logoPKLU },
];

// Catatan: kategori "Khusus" TIDAK lagi di-hardcode.
// Diambil otomatis dari backend lewat GET /ti_khusus/categories

const MONTHS = [
    { val: '01', name: 'Januari' }, { val: '02', name: 'Februari' },
    { val: '03', name: 'Maret' }, { val: '04', name: 'April' },
    { val: '05', name: 'Mei' }, { val: '06', name: 'Juni' },
    { val: '07', name: 'Juli' }, { val: '08', name: 'Agustus' },
    { val: '09', name: 'September' }, { val: '10', name: 'Oktober' },
    { val: '11', name: 'November' }, { val: '12', name: 'Desember' }
];

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
}

const TataIbadah = () => {
    const [allIbadahData, setAllIbadahData] = useState([]);
    const [activeTab, setActiveTab] = useState('minggu');
    const [activeSubTab, setActiveSubTab] = useState('pa');
    const [activeCategory, setActiveCategory] = useState(''); // '' = semua kategori (khusus tab "khusus")
    const [isLoading, setIsLoading] = useState(true);
    const [isFiltering, setIsFiltering] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const isSearching = searchTerm !== debouncedSearchTerm;

    // --- Kategori "Khusus" dari backend ---
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [categoriesLoaded, setCategoriesLoaded] = useState(false);

    useEffect(() => {
        let isCancelled = false;
        const fetchCategories = async () => {
            try {
                const response = await api.get('/ti_khusus/categories');
                const data = response.data?.data;
                // console.log(data)
                if (!isCancelled) setCategoryOptions(Array.isArray(data) ? data : []);
            } catch (error) {
                if (!isCancelled) setCategoryOptions([]);
            } finally {
                if (!isCancelled) setCategoriesLoaded(true);
            }
        };
        fetchCategories();
        return () => { isCancelled = true; };
    }, []);

    // 1. Fetching Data
    useEffect(() => {
        // Untuk tab "khusus", tunggu kategori selesai di-load dulu sebelum fetch,
        // supaya tidak loop dengan array kosong (race condition).
        if (activeTab === 'khusus' && !categoriesLoaded) return;

        let isCancelled = false;
        const fetchAllData = async () => {
            setIsLoading(true);
            setProgress(20);
            setError(null);

            try {
                let combined = [];

                if (activeTab === 'pelkat') {
                    const response = await api.get(`/ti_pelkat/list/${activeSubTab}`);
                    const rawData = response.data?.data;
                    combined = Array.isArray(rawData) ? rawData : (rawData?.data || []);
                } else if (activeTab === 'khusus') {
                    if (categoryOptions.length === 0) {
                        // Backend belum punya kategori terdaftar sama sekali
                        combined = [];
                    } else {
                        // Tidak ada endpoint list flat untuk ti_khusus — harus fetch per kategori lalu digabung
                        const promises = categoryOptions.map(cat =>
                            api.get(`/ti_khusus/list/${encodeURIComponent(cat)}`)
                        );
                        const results = await Promise.allSettled(promises);
                        results.forEach(res => {
                            if (res.status === 'fulfilled') {
                                const rawData = res.value.data?.data;
                                const items = Array.isArray(rawData) ? rawData : (rawData?.data || []);
                                combined = [...combined, ...items];
                            }
                            // kalau ada kategori yang gagal/404, dilewati saja (Promise.allSettled tidak melempar error keseluruhan)
                        });
                    }
                } else {
                    const target = MAIN_CATEGORIES.find(c => c.tab === activeTab);
                    const firstRes = await api.get(`/${target.endpoint}/list?page=1`);
                    let firstPageData = firstRes.data?.data?.data || [];
                    combined = [...firstPageData];

                    const lastPage = firstRes.data?.data?.last_page;
                    if (lastPage > 1) {
                        const promises = [];
                        for (let i = 2; i <= lastPage; i++) {
                            promises.push(api.get(`/${target.endpoint}/list?page=${i}`));
                        }
                        const results = await Promise.all(promises);
                        results.forEach(res => {
                            const pagedData = res.data?.data?.data || [];
                            combined = [...combined, ...pagedData];
                        });
                    }
                }

                if (!isCancelled) {
                    setAllIbadahData(combined);
                    setProgress(100);
                }
            } catch (err) {
                if (!isCancelled) {
                    setAllIbadahData([]);
                    setError('Gagal memuat data dari server.');
                }
            } finally {
                if (!isCancelled) setTimeout(() => setIsLoading(false), 500);
            }
        };

        fetchAllData();
        return () => { isCancelled = true; };
    }, [activeTab, activeSubTab, categoriesLoaded, categoryOptions]);

    // 2. Logic Filter (Judul & Tanggal)
    const years = useMemo(() => {
        const uniqueYears = allIbadahData
            .map(item => {
                const d = new Date(item.created_at);
                return !isNaN(d.getTime()) ? d.getFullYear().toString() : null;
            })
            .filter(y => y !== null);
        return [...new Set(uniqueYears)].sort((a, b) => b - a);
    }, [allIbadahData]);

    const filteredResults = useMemo(() => {
        return allIbadahData.filter(item => {
            const date = new Date(item.created_at);
            const isValidDate = !isNaN(date.getTime());
            const itemYear = isValidDate ? date.getFullYear().toString() : "";
            const itemMonthNum = isValidDate ? (date.getMonth() + 1).toString().padStart(2, '0') : "";
            const itemMonthName = isValidDate ? date.toLocaleDateString('id-ID', { month: 'long' }).toLowerCase() : "";
            const itemDay = isValidDate ? date.getDate().toString() : "";

            const fullText = `${itemDay} ${itemMonthName} ${itemYear} ${item.title || ""}`.toLowerCase();
            const searchLower = debouncedSearchTerm.toLowerCase();

            const matchesSearch = fullText.includes(searchLower);
            const matchesYear = filterYear ? itemYear === filterYear : true;
            const matchesMonth = filterMonth ? itemMonthNum === filterMonth : true;

            // Filter kategori hanya berlaku di tab "khusus"
            const matchesCategory = (activeTab === 'khusus' && activeCategory)
                ? (item.category || '').trim().toLowerCase() === activeCategory.trim().toLowerCase()
                : true;

            return matchesSearch && matchesYear && matchesMonth && matchesCategory;
        });
    }, [allIbadahData, debouncedSearchTerm, filterYear, filterMonth, activeTab, activeCategory]);

    // 3. Logic Pagination
    const totalPages = Math.ceil(filteredResults.length / itemsPerPage);

    const paginationRange = useMemo(() => {
        const delta = 1;
        const range = [];
        const rangeWithDots = [];
        let l;
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }
        for (let i of range) {
            if (l) {
                if (i - l === 2) rangeWithDots.push(l + 1);
                else if (i - l !== 1) rangeWithDots.push('...');
            }
            rangeWithDots.push(i);
            l = i;
        }
        return rangeWithDots;
    }, [currentPage, totalPages]);

    const currentData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredResults.slice(start, start + itemsPerPage);
    }, [filteredResults, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, filterYear, filterMonth, activeTab, activeSubTab, activeCategory]);

    // 4. Handlers
    const handleTabChange = (tabName) => {
        if (tabName !== activeTab) {
            setActiveTab(tabName);
            setActiveCategory(''); // reset kategori saat pindah tab utama
            handleResetFilter();
        }
    };

    const handleResetFilter = () => {
        setIsFiltering(true);
        setSearchTerm('');
        setFilterYear('');
        setFilterMonth('');
        setCurrentPage(1);
        setTimeout(() => setIsFiltering(false), 400);
    };

    const isFilterActive = searchTerm || filterYear || filterMonth || (activeTab === 'khusus' && activeCategory);

    // Info label untuk sub-kategori yang sedang aktif (dipakai di badge dropdown)
    const activePelkatInfo = SUB_PELKAT.find(s => s.tab === activeSubTab);

    return (
        <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 border-b-8 border-blue-600 mb-8 text-center">
                    <h1 className="text-4xl sm:text-5xl font-black text-gray-900">TATA IBADAH</h1>
                    <p className="text-gray-500 mt-2 text-lg font-medium">Kumpulan Panduan Liturgi dan Ibadah</p>
                </div>

                {/* Filter Card */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h2 className="text-center font-bold text-gray-700 mb-4 border-b pb-2">Pilih Kategori</h2>

                    {/* Main Tabs */}
                    <div className="flex flex-wrap justify-center gap-3 mb-6">
                        {MAIN_CATEGORIES.map((cat) => (
                            <button
                                key={cat.tab}
                                onClick={() => handleTabChange(cat.tab)}
                                className={`py-2 px-6 rounded-xl text-lg font-bold flex items-center transition-all ${activeTab === cat.tab ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-blue-50'}`}
                            >
                                <span dangerouslySetInnerHTML={{ __html: cat.icon }} className="mr-2" /> {cat.name}
                            </button>
                        ))}
                    </div>

                    {/* Dropdown Pelkat */}
                    {activeTab === 'pelkat' && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-xl animate-in fade-in zoom-in-95 duration-300">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 text-center">
                                Pilih Kategori Pelayanan (Pelkat)
                            </label>
                            <div className="relative max-w-sm mx-auto">
                                {activePelkatInfo && (
                                    <img
                                        src={activePelkatInfo.icon}
                                        alt=""
                                        className="w-6 h-6 object-contain absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                                    />
                                )}
                                <select
                                    value={activeSubTab}
                                    onChange={(e) => setActiveSubTab(e.target.value)}
                                    className="w-full pl-12 pr-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl font-bold text-gray-700 outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 appearance-none cursor-pointer"
                                >
                                    {SUB_PELKAT.map((sub) => (
                                        <option key={sub.tab} value={sub.tab}>{sub.name}</option>
                                    ))}
                                </select>
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▼</span>
                            </div>
                        </div>
                    )}

                    {/* Dropdown Kategori Khusus */}
                    {activeTab === 'khusus' && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-xl animate-in fade-in zoom-in-95 duration-300">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 text-center">
                                Pilih Kategori Ibadah Khusus
                            </label>
                            <div className="relative max-w-sm mx-auto">
                                <select
                                    value={activeCategory}
                                    onChange={(e) => setActiveCategory(e.target.value)}
                                    disabled={!categoriesLoaded}
                                    className="w-full pl-4 pr-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl font-bold text-gray-700 outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <option value="">
                                        {categoriesLoaded ? 'Semua Kategori' : 'Memuat kategori...'}
                                    </option>
                                    {categoryOptions.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">▼</span>
                            </div>
                        </div>
                    )}

                    {/* Search & Selects */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-6">
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2">🔍</span>
                            <input
                                type="text"
                                placeholder="Judul / Tanggal / Bulan..."
                                className="w-full pl-10 pr-4 py-2 bg-gray-50 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <select
                            className="p-2 bg-gray-50 border rounded-lg outline-none"
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                        >
                            <option value="">Semua Tahun</option>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <select
                            className="p-2 bg-gray-50 border rounded-lg outline-none"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                        >
                            <option value="">Semua Bulan</option>
                            {MONTHS.map(m => <option key={m.val} value={m.val}>{m.name}</option>)}
                        </select>
                    </div>

                    {/* TOMBOL HAPUS FILTER (Hanya muncul jika filter aktif) */}
                    {isFilterActive && (
                        <button
                            onClick={() => { handleResetFilter(); setActiveCategory(''); }}
                            className="mt-4 w-full py-2 text-red-500 text-xs font-bold uppercase tracking-widest hover:bg-red-50 rounded-lg transition-colors border border-dashed border-red-200"
                        >
                            ✕ Hapus Semua Filter
                        </button>
                    )}
                </div>

                {/* Content Area */}
                <div className="bg-white rounded-xl shadow-lg p-6 min-h-[400px] relative">
                    {error ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="text-red-500 text-5xl mb-4">⚠️</div>
                            <h3 className="text-xl font-bold text-gray-800">{error}</h3>
                            <button
                                onClick={() => window.location.reload()}
                                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Coba Lagi
                            </button>
                        </div>
                    ) : (isLoading || isFiltering) ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-30 rounded-xl">
                            <LoadingSpinner progress={isLoading ? progress : 100} />
                        </div>
                    ) : (
                        <PageTransitionWrapper>
                            <div className={isSearching ? 'opacity-40 blur-sm' : 'opacity-100'}>
                                {currentData.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 px-4 bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-50/50">
                                        <div className="relative mb-8">
                                            <div className="absolute inset-0 bg-blue-50 rounded-full scale-150 opacity-60 animate-pulse"></div>
                                            <div className="relative bg-gradient-to-br from-white to-gray-50 w-24 h-24 rounded-2xl shadow-sm border border-white flex items-center justify-center">
                                                <span className="text-5xl transform -rotate-12">🔎</span>
                                            </div>
                                        </div>
                                        <div className="text-center max-w-sm">
                                            <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-tight">
                                                {isFilterActive ? "Hasil Tidak Ditemukan" : "Belum Ada Data"}
                                            </h3>
                                            <p className="text-gray-500 font-medium leading-relaxed">
                                                {isFilterActive
                                                    ? `Kami tidak menemukan apa pun untuk "${searchTerm}". Coba gunakan kata kunci lain.`
                                                    : "Saat ini belum ada informasi yang tersedia."}
                                            </p>
                                        </div>
                                        {isFilterActive && (
                                            <button
                                                onClick={() => { handleResetFilter(); setActiveCategory(''); }}
                                                className="mt-10 flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-2xl font-bold transition-all shadow-lg active:scale-95"
                                            >
                                                🔄 Atur Ulang Pencarian
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                                            {currentData.map((item) => (
                                                <IbadahCard
                                                    key={item.id}
                                                    tataibadah={item} // Kirim seluruh objek item sebagai prop tataibadah
                                                    activeTab={activeTab}
                                                    activeSubTab={activeSubTab}
                                                />
                                            ))}
                                        </div>

                                        {/* PAGINATION UI */}
                                        {totalPages > 1 && (
                                            <div className="mt-12 flex justify-center items-center gap-2">
                                                <button
                                                    onClick={() => { setCurrentPage(prev => Math.max(prev - 1, 1)); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                                                    disabled={currentPage === 1}
                                                    className="h-10 w-10 flex items-center justify-center rounded-lg border bg-white disabled:opacity-30 hover:bg-gray-100"
                                                >‹</button>

                                                {paginationRange.map((page, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => { if (page !== '...') { setCurrentPage(page); window.scrollTo({ top: 300, behavior: 'smooth' }); } }}
                                                        className={`h-10 min-w-[40px] rounded-lg font-bold transition-all ${page === currentPage ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-gray-500 border hover:bg-gray-100'}`}
                                                    >
                                                        {page}
                                                    </button>
                                                ))}

                                                <button
                                                    onClick={() => { setCurrentPage(prev => Math.min(prev + 1, totalPages)); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                                                    disabled={currentPage === totalPages}
                                                    className="h-10 w-10 flex items-center justify-center rounded-lg border bg-white disabled:opacity-30 hover:bg-gray-100"
                                                >›</button>
                                            </div>
                                        )}
                                    </>
                                )
                                }
                            </div>
                        </PageTransitionWrapper>
                    )}
                </div>
            </div>
        </div>
    );
};

// Tambahkan 'desc' ke dalam destructuring props di sini
const IbadahCard = ({ tataibadah, activeTab, activeSubTab }) => (
    <Link
        to={activeTab === 'pelkat'
            ? `/tata-ibadah/pelkat/${activeSubTab}/${tataibadah.id}`
            : `/tata-ibadah/${activeTab}/${tataibadah.id}`
        }
        className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 flex flex-col group"
    >
        {/* Badge Kategori */}
        <div className="relative h-48 overflow-hidden bg-gray-100">
            <div className="absolute top-4 right-4 z-10">
                <span className={`text-white text-[11px] font-black px-4 py-1.5 rounded-full uppercase tracking-tighter shadow-md 
                    ${activeTab === 'pelkat' ? 'bg-yellow-600' : activeTab === 'khusus' ? 'bg-purple-600' : 'bg-blue-600'}`}>
                    {activeTab === 'pelkat'
                        ? activeSubTab.toUpperCase()
                        : activeTab === 'khusus' && tataibadah.category
                            ? tataibadah.category.toUpperCase()
                            : activeTab.toUpperCase()}
                </span>
            </div>

            {/* Image dengan Zoom Effect */}
            <div
                className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                style={{ backgroundImage: `url(${tataibadah.image_url || tICoverImage})` }}
            />
            <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors" />
        </div>

        {/* Konten Text */}
        <div className="p-5 flex text-center flex-col justify-between flex-grow">
            <div className="mb-2">
                <h3 className="text-2xl font-black text-gray-900 group-hover:text-blue-700 transition-colors uppercase leading-[1.2] tracking-tight line-clamp-2">
                    {tataibadah.title}
                </h3>
                {tataibadah.desc || tataibadah.description ? (
                    <div
                        className="text-gray-400 text-sm mt-3 line-clamp-2 italic font-medium leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: tataibadah.desc || tataibadah.description }}
                    />
                ) : (
                    <p className="text-gray-300 text-sm mt-3 italic font-medium">
                        Deskripsi tidak tersedia
                    </p>
                )}
            </div>

            {/* Footer Tanggal */}
            <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-center gap-2">
                    <span className="text-[15px] font-black text-green-400 uppercase tracking-[0.2em]">Dipublikasikan</span>
                    <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                    <p className="text-m font-bold text-blue-600 tracking-tight uppercase">
                        {new Date(tataibadah.created_at).toLocaleDateString('id-ID', {
                            day: 'numeric', month: 'long', year: 'numeric'
                        })}
                    </p>
                </div>
            </div>
        </div>
    </Link>
);

export default TataIbadah;